import { useState } from 'react';
import { Sparkle } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type AvatarVariant = 'user' | 'assistant' | 'system';

interface AvatarProps {
  /** Image source URL */
  src?: string | null;
  /** Alt text for the image */
  alt?: string;
  /** User name -- used for initials fallback and deterministic color */
  name?: string;
  /** Size preset */
  size?: AvatarSize;
  /** Chat variant -- controls background/icon style */
  variant?: AvatarVariant;
  /** Additional CSS classes */
  className?: string;
}

const sizeStyles: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

const sparkleSize: Record<AvatarSize, number> = {
  xs: 12,
  sm: 14,
  md: 18,
  lg: 22,
  xl: 28,
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const firstPart = parts[0];
  if (!firstPart) return '';
  const first = firstPart.charAt(0);
  if (parts.length === 1) return first.toUpperCase();
  const lastPart = parts[parts.length - 1];
  if (!lastPart) return first.toUpperCase();
  return (first + lastPart.charAt(0)).toUpperCase();
}

/** Generates a deterministic hue from a string for consistent avatar backgrounds. */
function stringToHue(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

export function Avatar({
  src,
  alt,
  name,
  size = 'md',
  variant,
  className,
}: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const showImage = src && !imgError;
  const initials = name ? getInitials(name) : '';
  const hue = name ? stringToHue(name) : 0;
  const label = alt || name || 'Avatar';

  // Assistant variant uses the Sparkle icon with purple chat-avatar tokens
  if (variant === 'assistant') {
    return (
      <div
        className={cn(
          'relative inline-flex items-center justify-center rounded-full shrink-0 font-semibold select-none overflow-hidden',
          sizeStyles[size],
          className,
        )}
        style={{
          backgroundColor: 'var(--color-chat-avatar-assistant-bg)',
          color: 'var(--color-chat-avatar-assistant-fg)',
        }}
        role="img"
        aria-label="Assistente IA"
      >
        <Sparkle size={sparkleSize[size]} weight="fill" />
      </div>
    );
  }

  // System variant -- neutral gray
  if (variant === 'system') {
    return (
      <div
        className={cn(
          'relative inline-flex items-center justify-center rounded-full shrink-0 font-semibold select-none overflow-hidden',
          'bg-bg-tertiary text-text-tertiary',
          sizeStyles[size],
          className,
        )}
        role="img"
        aria-label="Sistema"
      >
        <span aria-hidden="true">S</span>
      </div>
    );
  }

  // User variant with chat tokens (explicit variant='user')
  const isUserVariant = variant === 'user';

  // Determine background/foreground style
  const inlineStyle = (() => {
    if (showImage) return undefined;

    if (isUserVariant) {
      return {
        backgroundColor: 'var(--color-chat-avatar-user-bg)',
        color: 'var(--color-chat-avatar-user-fg)',
      };
    }

    // Default (no variant) -- deterministic hue from name
    if (name) {
      return {
        backgroundColor: `hsl(${hue}, 40%, 20%)`,
        color: `hsl(${hue}, 70%, 70%)`,
      };
    }

    return undefined;
  })();

  return (
    <div
      className={cn(
        'relative inline-flex items-center justify-center rounded-full shrink-0 font-semibold select-none overflow-hidden',
        !isUserVariant && !showImage && !name && 'bg-accent-subtle text-accent-400',
        sizeStyles[size],
        className,
      )}
      style={inlineStyle}
      role="img"
      aria-label={label}
    >
      {showImage ? (
        <img
          src={src}
          alt={label}
          onError={() => setImgError(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
    </div>
  );
}

export default Avatar;
